// This file was AdminGrid.tsx, now converted to AdminGrid.js for compatibility with JS codebase and ag-Grid
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import DataGrid from '../../common/components/DataGrid';
import { message, Button, Input } from 'antd';
import { AgGridColumn } from '@ag-grid-community/react';
import get from 'lodash/get';
import gql from 'graphql-tag';
import classNames from 'classnames';

function getScalarMutationVariables(row, field, value, idField = '_id', collectionName) {
  const vars = {
    collection: collectionName,
    documentId: row[idField],
    fieldKey: field,
  };
  if (typeof value === 'string') vars.value = value;
  else if (typeof value === 'number' && Number.isInteger(value)) vars.intValue = value;
  else if (typeof value === 'number') vars.floatValue = value;
  else if (typeof value === 'boolean') vars.boolValue = value;
  else if (value instanceof Date) vars.dateValue = value.toISOString();
  return vars;
}

// Helper to generate a generic adminCollection query for any collection
function getAdminCollectionQuery(collectionName) {
  return gql`
    query GetAdminCollection($offset: Int, $limit: Int) {
      admin {
        adminCollection(collection: "${collectionName}", offset: $offset, limit: $limit)
      }
    }
  `;
}

// Helper to recursively flatten all values in an object (for search)
function flattenValues(obj) {
  let result = [];
  for (const value of Object.values(obj)) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result = result.concat(flattenValues(value));
    } else if (Array.isArray(value)) {
      result = result.concat(value.map(v => (typeof v === 'object' ? flattenValues(v) : v)));
    } else if (value !== undefined && value !== null) {
      result.push(value);
    }
  }
  return result;
}

/**
 * AdminGrid: Generic admin data grid for any collection.
 * Props:
 * - collectionName: string (name of the collection in the query result)
 * - columns: ag-Grid column definitions
 * - query: GraphQL query to fetch data
 * - mutation: GraphQL mutation to update data
 * - idField: primary key field (default '_id')
 * - inputKey: mutation input key (default 'input')
 * - variables: query variables
 * - getMutationVariables: function(row, field, value) => mutation variables (optional)
 */
const AdminGrid = ({
  collectionName,
  columns,
  query,
  mutation,
  idField = '_id',
  inputKey = 'input',
  variables = { offset: 0, limit: 10000 },
  getMutationVariables,
}) => {
  // Use provided query or generate one for adminCollection
  const effectiveQuery = useMemo(() => {
    if (query) return query;
    return getAdminCollectionQuery(collectionName);
  }, [collectionName, query]);

  const { loading, data, refetch } = useQuery(effectiveQuery, { variables });
  const [updateMutation] = useMutation(mutation);
  const [saving, setSaving] = useState(false);
  const autoUpdate = false;
  const [pendingChanges, setPendingChanges] = useState([]);
  const pendingChangesRef = useRef(pendingChanges);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    pendingChangesRef.current = pendingChanges;
    // Force ag-Grid to refresh cells for immediate highlight
    if (gridApiRef.current) {
      gridApiRef.current.refreshCells({ force: true });
    }
  }, [pendingChanges]);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);
  const gridApiRef = useRef(null);

  // Extract rows from data returned by adminCollection
  const rows = useMemo(() => {
    if (!data) return [];
    const arr = get(data, 'admin.adminCollection', []);
    if (!Array.isArray(arr)) return [];
    if (!debouncedSearch) return arr;
    const searchLower = debouncedSearch.toLowerCase();
    return arr.filter(row => {
      const concat = flattenValues(row).join(' ').toLowerCase();
      return concat.includes(searchLower);
    });
  }, [data, debouncedSearch]);

  // Use provided getMutationVariables or default generic
  const mutationVarsBuilder =
    getMutationVariables ||
    ((row, field, value) => {
      if (!collectionName) {
        console.warn('AdminGrid: collectionName is missing. Mutation will fail.');
      }
      return getScalarMutationVariables(row, field, value, idField, collectionName || '');
    });

  // ag-Grid column definitions with inline editing and cell highlighting
  const gridColumns = useMemo(
    () =>
      columns.map(col => ({
        ...col,
        editable: col.editable !== false,
        cellClass: params => {
          if (!col.editable || autoUpdate) return '';
          // Use ref to get latest pendingChanges
          const found = pendingChangesRef.current.find(
            ch => ch.documentId === params.data[idField] && ch.fieldKey === col.field,
          );
          return found ? 'cell-changed' : '';
        },
      })),
    [columns, autoUpdate, idField],
  );

  // Handler for cell value changes
  const handleCellValueChanged = async params => {
    const col = columns.find(c => c.field === params.colDef.field);
    if (!col || col.editable === false) return;
    const field = col.field;
    const value = get(params.data, field);
    const rowId = params.data[idField];
    // Find the original value from the current rows
    const originalRow = rows.find(r => r[idField] === rowId);
    const oldValue = originalRow ? originalRow[field] : undefined;
    if (autoUpdate) {
      setSaving(true);
      try {
        const mutationVars = mutationVarsBuilder(params.data, field, value);
        if (!mutationVars.collection || !mutationVars.documentId || !mutationVars.fieldKey) {
          throw new Error(
            'Missing required mutation variables: collection, documentId, or fieldKey',
          );
        }
        await updateMutation({ variables: mutationVars });
        message.success('Updated successfully');
        refetch();
        setForceUpdate(f => f + 1);
      } catch (err) {
        message.error('Update failed');
      } finally {
        setSaving(false);
      }
    } else {
      // Manual mode: accumulate changes with old/new values
      setPendingChanges(prev => {
        // Remove any previous change for this row+field
        const filtered = prev.filter(ch => !(ch.documentId === rowId && ch.fieldKey === field));
        const mutationVars = mutationVarsBuilder(params.data, field, value);
        const newChanges = [
          ...filtered,
          {
            ...mutationVars,
            oldValue,
            newValue: value,
            rowId,
            field,
          },
        ];
        return newChanges;
      });
    }
  };

  // Handler for saving all pending changes
  const handleSave = async () => {
    setSaving(true);
    let errors = [];
    try {
      for (const change of pendingChanges) {
        try {
          await updateMutation({ variables: change });
        } catch (err) {
          errors.push(err);
        }
      }
      if (errors.length === 0) {
        message.success('All changes saved');
        setPendingChanges([]);
        await refetch();
        setForceUpdate(f => f + 1);
      } else {
        message.error('Failed to save some changes');
      }
    } catch (err) {
      // Do nothing, error already handled above
    } finally {
      setSaving(false);
    }
  };

  // if (loading || saving) return <Spin spinning={true} />;

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="w-full flex justify-between">
        <Input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 250, marginRight: 16, padding: 4 }}
        />
        <div>
          <Button
            type="primary"
            onClick={handleSave}
            disabled={pendingChanges.length === 0 || saving}
            loading={saving}
            style={{ marginRight: 8 }}
          >
            Save Changes ({pendingChanges.length})
          </Button>
          <Button
            onClick={async () => {
              setPendingChanges([]);
              if (typeof refetch === 'function') {
                await refetch();
              }
              setForceUpdate(f => f + 1);
            }}
            disabled={pendingChanges.length === 0 || saving}
          >
            Cancel
          </Button>
        </div>
      </div>
      <div className={classNames('w-full h-full border border-solid border-gray-300 rounded-md', {
        'pointer-events-none opacity-50': loading || saving,
      })}>
        <DataGrid
          rowData={rows}
          height="700px"
          defaultColDef={{ editable: true, sortable: true, resizable: true, filter: true }}
          suppressClickEdit={false}
          stopEditingWhenCellsLoseFocus={true}
          onCellValueChanged={handleCellValueChanged}
          onGridReady={params => {
            if (params && params.api) gridApiRef.current = params.api;
          }}
          key={forceUpdate}
        >
          {gridColumns.map((col, idx) => (
            <AgGridColumn key={col.field || idx} {...col} />
          ))}
        </DataGrid>
        {/* Highlight style for changed cells */}
        <style>{`
        .ag-theme-balham .cell-changed {
          background-color: #ffe066 !important;
          transition: background 0.3s;
        }
      `}</style>
      </div>
    </div>
  );
};

export default AdminGrid;
